import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Table, Empty, Space, Skeleton } from 'antd';
import { useIsMobile } from '@/lib/breakpoints';
/** Renders as an Antd Table on md+, and as stacked cards on mobile.
 *
 *  Mobile design principle: each row becomes a self-contained card with
 *  the most important 2-3 fields prominent, secondary fields in a
 *  smaller "meta row", and actions along the bottom edge.
 */
export function ResponsiveTable({ mobileRender, mobileRowKey, mobileActions, ...tableProps }) {
    const isMobile = useIsMobile();
    if (!isMobile) {
        return _jsx(Table, { ...tableProps });
    }
    const rows = (tableProps.dataSource ?? []);
    if (tableProps.loading) {
        return (_jsx(Space, { direction: "vertical", style: { width: '100%' }, size: 12, children: [0, 1, 2].map((i) => (_jsx("div", { style: cardStyle, children: _jsx(Skeleton, { active: true, paragraph: { rows: 2 } }) }, i))) }));
    }
    if (rows.length === 0) {
        return (_jsx("div", { style: { padding: 32 }, children: _jsx(Empty, { description: "Belum ada data" }) }));
    }
    const rowKey = tableProps.rowKey;
    const getKey = (r, i) => {
        if (mobileRowKey)
            return mobileRowKey(r);
        if (typeof rowKey === 'function')
            return String(rowKey(r));
        if (typeof rowKey === 'string')
            return String(r[rowKey] ?? i);
        return String(i);
    };
    return (_jsx(Space, { direction: "vertical", style: { width: '100%' }, size: 10, children: rows.map((row, i) => (_jsxs("div", { style: cardStyle, children: [_jsx("div", { children: mobileRender(row, i) }), mobileActions && (_jsx("div", { style: {
                        marginTop: 12,
                        paddingTop: 10,
                        borderTop: '1px dashed #E5E7EB',
                        display: 'flex',
                        gap: 8,
                        justifyContent: 'flex-end',
                        flexWrap: 'wrap',
                    }, children: mobileActions(row) }))] }, getKey(row, i)))) }));
}
const cardStyle = {
    background: '#FFFFFF',
    border: '1px solid #F3F4F6',
    borderLeft: '3px solid #B91C1C',
    borderRadius: 10,
    padding: '14px 14px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
};
/** Tiny helpers for consistent mobile card typography. */
export function CardKV({ label, value, right }) {
    return (_jsxs("div", { style: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 8,
            padding: '3px 0',
            fontSize: 13,
        }, children: [_jsx("span", { style: { color: '#6B7280', fontSize: 12, whiteSpace: 'nowrap' }, children: label }), _jsx("span", { style: {
                    color: '#111827',
                    fontVariantNumeric: right ? 'tabular-nums' : undefined,
                    textAlign: right ? 'right' : 'left',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }, children: value })] }));
}
export function CardTitleRow({ primary, secondary, status, }) {
    return (_jsxs("div", { style: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 12,
            marginBottom: 8,
        }, children: [_jsxs("div", { style: { minWidth: 0, flex: 1 }, children: [_jsx("div", { style: {
                            fontWeight: 600,
                            color: '#111827',
                            fontSize: 14,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }, children: primary }), secondary && _jsx("div", { style: { color: '#6B7280', fontSize: 12, marginTop: 2 }, children: secondary })] }), status && _jsx("div", { style: { flexShrink: 0 }, children: status })] }));
}
